import React, {useMemo, useState} from 'react';
import {t} from 'i18next';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Box,
  Divider,
} from '@mui/material';
import './DataImportsCards.scss';
import {ICEDatasetTileItemType} from '../../../../api/types/ingestionApi.type';
import {formatDateTime} from '../../../../utils/utils';
import StripeProgressBar from '../component/StripeProgressBar/StripeProgressBar';
import ArrActionButton from '../../../common/arr-action-button/ArrActionButton';
import {useNavigate} from 'react-router-dom';
import {useGetReportDocumentMasterDocId} from '../../../../hooks/reporting/useReporting';
import {IGetReportMasterDocListIdRequest} from '../../../../api/types/reportingApi.type';
import ArrDocMaxLimitWarning from '../../../common/arr-doc-max-limit-warning/ArrDocMaxLimitWarning';
import {
  DocMaxLimitDestination,
  DocMaxLimitDestinationCode,
  SourceTypeCode,
} from '../../../../shared/constants';
import {
  useCreateMasterDocList,
  useDocumentList,
  useDocumentViewerListId,
} from '../../../../hooks/document/useDocument';
import {
  DocExplorerMasterRequest,
  DocumentListRequestPayload,
} from '../../../../api/types/document.type';
import {clearDocViewState} from '../../../document-viewer/docViewerUtils';
import {useDispatch} from 'react-redux';
import {
  storeDocumentListMasterId,
  storeSourceTypeCode,
} from '../../../../redux/slice/docViewSlice';
import {
  setDocListBackNavigation,
  setDocListId,
} from '../../../../redux/slice/docListSlice';
import {createDocumentListRequestPayload} from '../../../productions/create-new-production/CreateNewProductionUtils';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {createDocumentViewerListRequestPayloadFromAssignments} from '../../../assignments/AssignmentsUtils';
import {useAnalytics} from '../../../../hooks/useAnalytics';
import {trackActionMenuSelection} from '../../../../utils/analyticsUtils';

interface DataImportsCardsProps {
  data: ICEDatasetTileItemType;
  dataTestId: string;
}

const dataImportNavigationDestination: Partial<
  Record<SourceTypeCode.DOCVIEW | SourceTypeCode.DOCLIST, DocMaxLimitDestination>
> = {
  [SourceTypeCode.DOCVIEW]: DocMaxLimitDestinationCode.DATASETDOCVIEW,
  [SourceTypeCode.DOCLIST]: DocMaxLimitDestinationCode.DATASETDOCLIST,
};

const getDataImportNavigationDestination = (
  navigationType?: SourceTypeCode
): DocMaxLimitDestination | undefined => {
  if (
    navigationType === SourceTypeCode.DOCVIEW ||
    navigationType === SourceTypeCode.DOCLIST
  ) {
    return dataImportNavigationDestination[navigationType];
  }
  return undefined;
};

const DataImportsCards: React.FC<DataImportsCardsProps> = ({
  data,
  dataTestId,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {trackGAEvent} = useAnalytics();

  const [showDocLimitWarning, setShowDocLimitWarning] = useState(false);
  const [navigationType, setNavigationType] = useState<SourceTypeCode>();
  const [selectedActionId, setSelectedActionId] = useState<string>('');
  const {mutate: reportMasterDocListId} = useGetReportDocumentMasterDocId(
    (response) => {
      const source = response?.reportMasterDoclistId
        ? [String(response.reportMasterDoclistId)]
        : [];

      navigate('/Report/Tally', {
        state: {
          navigationSourceType: 'INGESTION',
          source,
          sourceName: 'DOCLIST',
        },
      });
    },
    () => {
      setIsLoading(false);
    }
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {mutate: fetchDocumentList} = useDocumentList(
    (response) => {
      clearDocViewState();
      dispatch(storeDocumentListMasterId(response.docId as string));
      dispatch(storeSourceTypeCode(SourceTypeCode.INGESTION));
      dispatch(setDocListId(Number(response.docId)));
      const backPath = `${window.location.pathname}${window.location.search}`;
      dispatch(
        setDocListBackNavigation({
          label: t('userManagement.editUser.functionality.ingestions'),
          path: backPath,
        })
      );

      setTimeout(() => {
        navigate('/Document/DocList');
      }, 0);
    },
    () => {
      setIsLoading(false);
    }
  );

  const handleNavigateToTally = () => {
    setIsLoading(true);
    const reportPayload: IGetReportMasterDocListIdRequest = {
      sourceTypeCode: 'INGESTION',
      destinationContextTypeCode: 'TALLY',
      totalDocumentToView: -1,
      sourceDetails: [
        {
          sourceID: data.datasetID,
        },
      ],
      isDocumentCount: false,
    };

    reportMasterDocListId(reportPayload);
  };

  const handleNavigateToDocList = () => {
    setNavigationType(SourceTypeCode.DOCLIST);
    setShowDocLimitWarning(true);
    setSelectedActionId('ViewInDocList');
  };

  const startDocumentNavigation = (totalDocumentToView: number) => {
    setIsLoading(true);
    if (selectedActionId === 'ViewInDocView') {
      const payload = createDocumentViewerListRequestPayloadFromAssignments({
        sourceID: data.datasetID,
        totalDocumentToView,
        objSourceOverrides: {
          sourceTypeCode: SourceTypeCode.INGESTION,
        },
      });
      setShowDocLimitWarning(false);
      fetchDocumentViewerListID(payload);
    }

    if (selectedActionId === 'ViewInDocList') {
      const payload = createDocumentListRequestPayload({
        sourceId: data.datasetID,
        sourceTypeCode: SourceTypeCode.INGESTION,
        docViewSourceTypeCode: SourceTypeCode.INGESTION,
        sourceInfoOverrides: {
          destinationContextTypeCode: 'DOCLIST',
          totalDocumentToView,
        },
        docViewSessionOverrides: {
          docsForCodeSame: [''],
        },
      });
      setShowDocLimitWarning(false);
      fetchDocumentList(payload);
    }
  };

  const {mutate: fetchDocumentViewerListID} = useDocumentViewerListId(
    (response) => {
      clearDocViewState();
      dispatch(
        storeDocumentListMasterId(
          String(response.docViewSessionObject.documentListMasterID)
        )
      );
      dispatch(storeSourceTypeCode(SourceTypeCode.INGESTION));
      dispatch(setDocListId(Number(response.documentListID)));

      setTimeout(() => {
        navigate('/DocumentViewer/DocView');
      }, 0);
    },
    () => {
      setIsLoading(false);
    }
  );

  const handleNavigateToDocView = () => {
    setNavigationType(SourceTypeCode.DOCVIEW);
    setShowDocLimitWarning(true);
    setSelectedActionId('ViewInDocView');
  };

  const viewSetInOption = useMemo(() => {
    return [
      {
        label: t('dataImports.docView'),
        id: '1',
        onClick: handleNavigateToDocView,
      },
      {
        label: t('dataImports.docList'),
        id: '2',
        onClick: handleNavigateToDocList,
      },
      {label: t('dataImports.tally'), id: '3', onClick: handleNavigateToTally},
    ];
  }, [handleNavigateToDocView, handleNavigateToDocList, handleNavigateToTally]);

  const renderStates = (title: string, count: number) => {
    return (
      <Box className="stat-item">
        <Typography
          className="typo-text label3_size title-label"
          tabIndex={0}
          data-testid={`data-imports-cards-title-${dataTestId}`}
        >
          {title}
        </Typography>
        <Typography
          className="typo-text subhead2_size value-label"
          tabIndex={0}
          data-testid={`data-imports-cards-typography-tcount-${dataTestId}`}
        >
          {count.toLocaleString()}
        </Typography>
      </Box>
    );
  };

  const dataSetTypeCaption = useMemo(() => {
    if (data.isMappedSet) {
      return t('dataImports.mappedSet');
    }
    if (data.isImportSet) {
      return t('dataImports.importSet');
    }
    if (data.isApoUpdate) {
      return t('dataImports.apoUpdate');
    }
    return t('dataImports.uploadSet');
  }, [data, t]);

  return (
    <Card className="data-import-card">
      <CardHeader
        className="data-import-card-header"
        title={
          <Typography
            className="typo-text label3_size import-set-title"
            tabIndex={0}
            data-testid={`data-imports-cards-typography-caption-${dataTestId}`}
          >
            {dataSetTypeCaption}
          </Typography>
        }
      />
      <CardContent className="data-import-card-content">
        <Typography
          className="typo-text subhead3_size data-import-name"
          tabIndex={0}
          title={data.name}
          data-testid={`data-imports-cards-typography-name-${dataTestId}`}
        >
          {data.name}
        </Typography>

        {data.datasetStatus === 'PUBLISHCOMPLETE' ? (
          <Box className="completed-view-set-container">
            <ArrActionButton
              menuItems={viewSetInOption}
              size="small"
              direction="bottom-right"
              title={t('dataImports.viewSetIn')}
              dataTestId="view-set-in-btn"
              autoFocusMenu
              disableAutoFocusItem={false}
              onActionPick={(item) =>
                trackActionMenuSelection(trackGAEvent, item)
              }
            />
          </Box>
        ) : (
          <Box className="progress-status-container">
            <Typography
              className="typo-text label3_size status-label"
              tabIndex={0}
              data-testid={`data-imports-cards-typography-current-status-${dataTestId}`}
            >
              {data.currentStatus}
            </Typography>
            <StripeProgressBar progress={data.totalPercentComplete} />
          </Box>
        )}

        <Box className="stats-row">
          {renderStates(t('dataImports.uploaded'), data.uploadedDocCount)}
          {renderStates(t('dataImports.released'), data.publishedDocCount)}
          {renderStates(t('dataImports.errors'), data.errorDocCount)}
        </Box>

        <Divider className="divider" />

        <Box className="meta-info">
          <Box>
            <Typography
              className="typo-text label3_size title-label"
              tabIndex={0}
              data-testid={`data-imports-cards-last-modified`}
            >
              {t('dataImports.lastModified')}
            </Typography>
            <Typography
              className="typo-text body4_size value-label"
              tabIndex={0}
              title={`${data.lastModifiedBy} ${formatDateTime(
                data.lastModifiedOn
              )}`}
              data-testid={`data-imports-cards-typography-modified-${dataTestId}`}
            >
              {data.lastModifiedBy} {formatDateTime(data.lastModifiedOn)}
            </Typography>
          </Box>
          <Box>
            <Typography
              className="typo-text label3_size title-label"
              tabIndex={0}
              data-testid={`data-imports-cards-last-status`}
            >
              {t('dataImports.lastStatus')}
            </Typography>
            <Typography
              className="typo-text body4_size value-label"
              tabIndex={0}
              title={`${data.lastStatusText} ${formatDateTime(
                data.lastModifiedOn
              )}`}
              data-testid={`data-imports-cards-typography-last-status-${dataTestId}`}
            >
              {data.lastStatusText} {formatDateTime(data.lastModifiedOn)}
            </Typography>
          </Box>
        </Box>
      </CardContent>
      <ArrLoadingSpinner overlay isOpen={isLoading} />
      <ArrDocMaxLimitWarning
        totalDocumentCount={data.publishedDocCount}
        show={showDocLimitWarning}
        destination={getDataImportNavigationDestination(navigationType)}
        onCancel={() => setShowDocLimitWarning(false)}
        onContinue={(totalDocumentToView: number) =>
          startDocumentNavigation(totalDocumentToView)
        }
      />
    </Card>
  );
};

export default DataImportsCards;
